import { useState } from "react"
import { Navigate, useLocation, useNavigate } from "react-router"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { z } from "zod"

import { ApiError } from "@/api/client"
import { AUTH_ME_QUERY_KEY, useLogin } from "@/api/hooks/auth"
import { useAuth } from "@/auth/AuthProvider"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

const schema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
})

type LoginFormValues = z.infer<typeof schema>

/**
 * React port of `templates/login.html` (T3.9). Public route — sits
 * outside `<AppShell/>` and `<ProtectedRoute/>`. Cookie-mode auth via
 * `useLogin()`; no `localStorage` writes.
 *
 * Parity-fixed gotchas from `docs/migration/parity/login.md`:
 *   - Submit button is disabled while the mutation is in flight
 *     (Jinja allowed double-submit).
 *   - Already-authenticated visitors get `<Navigate/>`'d before the
 *     form is rendered, so the brief flash of the login form on
 *     reload is gone (Jinja races a `/auth/me` ping against paint).
 *   - `autocomplete` attrs added for password manager support.
 */
export default function LoginPage() {
  const { user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const loginMutation = useLogin()
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { username: "", password: "" },
  })

  // Show the form while /auth/me resolves — avoids a blank page during
  // slow API/DB responses (login still works if the probe is down).
  if (user) return <Navigate to="/dashboard" replace />

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null)
    try {
      await loginMutation.mutateAsync(values)
      // useLogin().onSuccess invalidates the auth-me query, but the
      // currently-cached null won't auto-refetch on its own (no active
      // observer here yet). Force a refetch so navigating into the
      // protected shell sees the freshly-set user.
      await queryClient.refetchQueries({ queryKey: AUTH_ME_QUERY_KEY })
      const from = (location.state as { from?: { pathname: string } } | null)
        ?.from?.pathname
      navigate(from ?? "/dashboard", { replace: true })
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.message)
      } else {
        setSubmitError("Network error. Please try again.")
      }
    }
  })

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-bg text-brand-fg px-4">
      <Card className="w-full max-w-md bg-[#3a3a3a] border-[#4a4a4a] rounded-2xl p-12 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
        <h1 className="text-center text-3xl font-semibold text-brand-fg mb-2">
          🚀 Welcome Back
        </h1>
        <p className="text-center text-sm text-[#b0b0b0] mb-8">
          Log into your ARCH HUB account
        </p>

        {submitError ? (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-[#663333] bg-[#4a2a2a] px-3 py-3 text-sm text-[#f87171]"
          >
            {submitError}
          </div>
        ) : null}

        <form onSubmit={onSubmit} noValidate>
          <div className="mb-6">
            <Label
              htmlFor="username"
              className="mb-2 block text-brand-fg font-semibold"
            >
              Username
            </Label>
            <Input
              id="username"
              type="text"
              autoComplete="username"
              aria-invalid={errors.username ? "true" : "false"}
              className="h-12 rounded-lg border-2 border-[#555555] bg-[#2d2d2d] px-3 text-base text-brand-fg focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-brand-accent"
              {...register("username")}
            />
            {errors.username ? (
              <p className="mt-1 text-sm text-[#f87171]">
                {errors.username.message}
              </p>
            ) : null}
          </div>

          <div className="mb-6">
            <Label
              htmlFor="password"
              className="mb-2 block text-brand-fg font-semibold"
            >
              Password
            </Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              aria-invalid={errors.password ? "true" : "false"}
              className="h-12 rounded-lg border-2 border-[#555555] bg-[#2d2d2d] px-3 text-base text-brand-fg focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-brand-accent"
              {...register("password")}
            />
            {errors.password ? (
              <p className="mt-1 text-sm text-[#f87171]">
                {errors.password.message}
              </p>
            ) : null}
          </div>

          <Button
            type="submit"
            disabled={loginMutation.isPending}
            className="w-full h-12 rounded-lg text-base font-semibold text-white border-0 bg-gradient-to-br from-[#8b9aff] to-[#667eea] hover:opacity-95 hover:-translate-y-0.5 hover:shadow-[0_10px_20px_rgba(139,154,255,0.4)] transition-all"
          >
            {loginMutation.isPending ? "Logging in…" : "Login"}
          </Button>
        </form>
      </Card>
    </div>
  )
}
